import { useState } from "preact/hooks";
import { Compass, Eye, EyeOff, Shield } from "preact-feather";
import {
  Alert,
  Button,
  Checkbox,
  Input,
} from "@silverbulletmd/silverbullet/ui";
import { PlainleafSkinPicker } from "../../components/plainleaf_skin_picker.tsx";
import { usePlainleafSkin } from "../../lib/plainleaf_skin.ts";

export type LoginValues = {
  username: string;
  password: string;
  rememberMe: boolean;
  clientEncryption: boolean;
};

/**
 * The login form, shared by a space's own login page and the Space Manager.
 * Purely presentational: it collects credentials and hands them to `onSubmit`,
 * which is where the two differ (one posts to a space's `.auth`, the other to
 * the Space Manager's JSON API).
 *
 * The optional fields are opt-in per caller rather than always-on. Client
 * encryption in particular only belongs on a space's login page: the key is
 * handed to that space's service worker, and the Space Manager has none.
 */
export function LoginForm({
  title,
  error,
  busy,
  rememberMeDays,
  clientEncryption = false,
  clientEncryptionHint,
  initialClientEncryption = false,
  initialRememberMe = true,
  initialUsername = "",
  usernameReadOnly = false,
  submitLabel = "进入我的笔记",
  subtitle = "把想法、旅程和待办收进自己的知识岛屿",
  children,
  onSubmit,
}: {
  title: preact.ComponentChildren;
  error?: string;
  busy?: boolean;
  /** Show "Remember me (N days)" when set. */
  rememberMeDays?: number;
  /** Show the client-encryption opt-in. */
  clientEncryption?: boolean;
  /** Explanatory line under the client-encryption option, when it needs one. */
  clientEncryptionHint?: string;
  initialClientEncryption?: boolean;
  /** Keep the normal personal-device login sticky unless explicitly unchecked. */
  initialRememberMe?: boolean;
  initialUsername?: string;
  usernameReadOnly?: boolean;
  submitLabel?: string;
  subtitle?: string;
  /** Extra content below the form (e.g. the login page's footer link). */
  children?: preact.ComponentChildren;
  onSubmit: (values: LoginValues) => void;
}) {
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [rememberMe, setRememberMe] = useState(initialRememberMe);
  const [encrypt, setEncrypt] = useState(initialClientEncryption);
  const [skin, setSkin] = usePlainleafSkin();

  return (
    <main class="pl-login-scene">
      <div class="pl-login-clouds is-back" aria-hidden="true" />
      <div class="pl-login-clouds is-front" aria-hidden="true" />
      <div class="pl-login-land" aria-hidden="true" />
      <section class="pl-login-panel" aria-label="Plainleaf 登录">
        <div class="pl-login-brand">
          <span class="pl-login-brand-mark" aria-hidden="true">
            <Compass size={22} />
          </span>
          <span>
            <strong>Plainleaf</strong>
            <small class="pl-login-brand-cloudleaf">云叶冒险日志</small>
            <small class="pl-login-brand-classic">原生 Markdown 笔记</small>
          </span>
        </div>
        <form
          class="flow pl-login-form"
          id="login"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              username,
              password,
              rememberMe,
              clientEncryption: clientEncryption && encrypt,
            });
          }}
        >
          <header class="pl-login-heading">
            <span class="pl-login-eyebrow">冒险档案</span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </header>
          {error && <Alert variant="error">{error}</Alert>}
          <div>
            <label for="username">用户名</label>
            <Input
              id="username"
              name="username"
              autocapitalize="off"
              autocomplete="username"
              autocorrect="off"
              autoFocus={!usernameReadOnly}
              readOnly={usernameReadOnly}
              value={username}
              onInput={(event) => setUsername(event.currentTarget.value)}
            />
          </div>
          <div>
            <label for="password">密码</label>
            <div class="password-field">
              <Input
                id="password"
                name="password"
                type={revealed ? "text" : "password"}
                autocomplete="current-password"
                autoFocus={usernameReadOnly}
                value={password}
                onInput={(event) => setPassword(event.currentTarget.value)}
              />
              <Button
                type="button"
                id="togglePassword"
                title={revealed ? "隐藏密码" : "显示密码"}
                aria-label={revealed ? "隐藏密码" : "显示密码"}
                onClick={() => setRevealed((shown) => !shown)}
              >
                {revealed ? <EyeOff size={17} /> : <Eye size={17} />}
              </Button>
            </div>
          </div>
          {rememberMeDays !== undefined && (
            <div class="checkbox-wrapper">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.currentTarget.checked)}
              />
              <label for="rememberMe">保持登录（{rememberMeDays} 天）</label>
            </div>
          )}
          {clientEncryption && (
            <details class="pl-login-security">
              <summary>
                <Shield size={15} />
                安全选项
              </summary>
              <div class="checkbox-wrapper">
                <Checkbox
                  id="clientEncryption"
                  checked={encrypt}
                  onChange={(event) => setEncrypt(event.currentTarget.checked)}
                />
                <label for="clientEncryption">启用客户端加密</label>
              </div>
              {clientEncryptionHint && (
                <span class="sb-help-text">{clientEncryptionHint}</span>
              )}
            </details>
          )}
          <div class="pl-login-submit">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "正在处理…" : submitLabel}
            </Button>
          </div>
          {children}
        </form>
        <footer class="pl-login-skin-control">
          <span>界面皮肤</span>
          <PlainleafSkinPicker skin={skin} onChange={setSkin} compact />
        </footer>
      </section>
      <div class="pl-login-scene-caption" aria-hidden="true">
        <strong>云叶港</strong>
        <span>把今天写进旅途</span>
      </div>
    </main>
  );
}
